import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Menu, X, Mail, Phone } from 'lucide-react';

/* ------------------------------------------------------------------ *
 * Landing ReportFIUS — porte do pacote ReportFIUS_site_package.
 * Paleta e tipografia da marca FIUS:
 * Navy #111D30 · Azul #009EDB · Petróleo #004561 · Laranja #EA5627
 * ------------------------------------------------------------------ */

const C = {
  blue: '#009EDB',
  navy: '#111D30',
  gray: '#6D6E71',
  light: '#F3F3F3',
  petrol: '#004561',
  charcoal: '#282A2B',
  orange: '#EA5627',
  border: '#DFE3E8',
};

const EMAIL = 'compliance@fius.com.br';
const PHONE_HREF = '+5519982094955';
const PHONE_LABEL = '(19) 98209 4955';
const MEETING_URL = 'https://meetings.hubspot.com/isadora-diniz';
const VIDEO_EMBED_URL = 'https://www.youtube-nocookie.com/embed/kJQxlvnJpH8?rel=0';
const VIDEO_WATCH_URL = 'https://youtu.be/kJQxlvnJpH8';

/* Assets do pacote, servidos localmente (nada de CDN externo). */
const IMG = (name) => `${process.env.PUBLIC_URL || ''}/images/landing/${name}`;

const NAV_LINKS = [
  { label: 'Plataforma', href: '#plataforma' },
  { label: 'Como funciona', href: '#como-funciona' },
  { label: 'Diferenciais', href: '#diferenciais' },
  { label: 'Planos', href: '#planos' },
  { label: 'Vídeo', href: '#video' },
  { label: 'FAQ', href: '#faq' },
];

/* ------------------------------ Primitivos ------------------------------ */

/* Duas defesas contra modern-design-system.css, que redefine utilitárias do
   Tailwind depois delas no bundle (é global: entra pelo import do ModernDashboard):
   1. `.grid{gap:24px}` engole qualquer `gap-` — por isso toda grade usa `!gap-*`
      (em container flex o `gap-` normal continua valendo);
   2. `.sm|md|lg:grid-cols-N` de lá usam `max-width`, o inverso do mobile-first do
      Tailwind — por isso as colunas vão em valor arbitrário, que ninguém redefine. */

/** `--container` do pacote: 1180px com 20px de respiro nas laterais. */
const Container = ({ children, className = '', style, id }) => (
  <div
    id={id}
    className={className}
    style={{ width: 'min(1180px, calc(100% - 40px))', marginInline: 'auto', ...style }}
  >
    {children}
  </div>
);

const Reveal = ({ children, delay = 0, className = '', style, id }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      id={id}
      className={className}
      style={style}
      initial={{ opacity: 0, y: 15 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

/** Scroll manual: âncora nativa falha se o hash já está na URL ou se o router interfere. */
const scrollToHash = (e, href) => {
  const el = document.getElementById(href.slice(1));
  if (!el) return;
  e.preventDefault();
  el.scrollIntoView({ behavior: 'smooth' });
};

const H1 = ({ children, className = '' }) => (
  <h1
    className={className}
    style={{ fontSize: 'clamp(42px,6vw,76px)', lineHeight: 0.98, letterSpacing: '-0.035em', fontWeight: 900 }}
  >
    {children}
  </h1>
);

const H2 = ({ children, className = '', dark = false }) => (
  <h2
    className={className}
    style={{
      fontSize: 'clamp(32px,4vw,48px)',
      lineHeight: 1.04,
      letterSpacing: '-0.025em',
      fontWeight: 800,
      color: dark ? '#fff' : C.navy,
    }}
  >
    {children}
  </h2>
);

const H3 = ({ children, className = '', dark = false, style }) => (
  <h3
    className={className}
    style={{ fontSize: 24, lineHeight: 1.15, fontWeight: 700, color: dark ? '#fff' : C.navy, ...style }}
  >
    {children}
  </h3>
);

const Lead = ({ children, className = '', style }) => (
  <p className={className} style={{ fontSize: 'clamp(19px,2.2vw,24px)', lineHeight: 1.42, ...style }}>
    {children}
  </p>
);

const Eyebrow = ({ children, className = '' }) => (
  <span
    className={
      'inline-flex items-center gap-2.5 text-[13px] font-extrabold uppercase tracking-[0.12em] text-[#009EDB] ' +
      className
    }
  >
    <span aria-hidden="true" className="h-2 w-2 shrink-0 bg-[#009EDB]" />
    {children}
  </span>
);

const BTN_BASE =
  'inline-flex cursor-pointer items-center justify-center gap-[9px] rounded-full border-0 px-[22px] py-[13px] ' +
  'text-[15px] font-extrabold no-underline transition duration-200';

const BTN_VARIANTS = {
  primary: 'bg-[#009EDB] text-white hover:-translate-y-px hover:shadow-[0_8px_20px_rgba(0,158,219,0.24)]',
  light: 'border border-[#DFE3E8] bg-white text-[#111D30] hover:-translate-y-px',
  ghost: 'border border-white/[0.28] bg-transparent text-white hover:border-white/60',
  accent: 'bg-[#EA5627] text-white hover:-translate-y-px',
};

const Btn = ({ children, href, variant = 'primary', className = '', onClick }) => {
  const cls = `${BTN_BASE} ${BTN_VARIANTS[variant]} ${className}`;
  const external = /^https?:/.test(href || '');
  return (
    <a
      href={href}
      className={cls}
      onClick={(e) => {
        if (href && href.startsWith('#')) scrollToHash(e, href);
        if (onClick) onClick(e);
      }}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
};

/** Card branco padrão do pacote. */
const CARD =
  'rounded-[6px] border border-[#DFE3E8] bg-white p-[26px] shadow-[0_1px_2px_rgba(17,29,48,0.06)]';

const Chip = ({ children, onDark = false }) => (
  <span
    className={
      'inline-flex rounded-full px-3 py-[5px] text-xs font-extrabold ' +
      (onDark
        ? 'border border-white/10 bg-white/[0.08] text-white'
        : 'bg-[#009EDB]/[0.12] text-[#009EDB]')
    }
  >
    {children}
  </span>
);

/* ------------------------------- Conteúdo ------------------------------- */

const BENEFITS = [
  ['estrategia-4.svg', 'Reduz perdas financeiras', 'Detecta irregularidades antes que gerem impactos relevantes.'],
  ['projetos-8.svg', 'Fortalece a governança', 'Aumenta a transparência, a confiança e a cultura ética.'],
  ['seguro-4.svg', 'Protege a reputação', 'Permite atuação rápida antes que o problema se torne público.'],
  ['estrategia-2.svg', 'Gera inteligência', 'Transforma relatos em informação para aprimorar processos.'],
  ['empresarial-3.svg', 'Ambiente organizacional', 'Incentiva diálogo, respeito e prevenção de condutas inadequadas.'],
  ['direito-4.svg', 'Conformidade regulatória', 'Apoia o atendimento às exigências legais e boas práticas.'],
];

const TIMELINE = [
  ['2013', 'Boa prática', 'Lei Anticorrupção', 'Incentiva mecanismos de denúncia como elemento do Programa de Integridade.'],
  ['2022', 'Requisito', 'Decreto nº 11.129', 'Exige canal de denúncias para avaliação da efetividade do Programa de Integridade.'],
  ['2022', 'Obrigatório', 'Lei nº 14.457', 'Prevê canal para denúncias de assédio e violência no ambiente de trabalho.'],
  ['2023', 'Obrigatório', 'Lei nº 14.611', 'Reforça mecanismos de denúncia relacionados à igualdade salarial e aos critérios remuneratórios.'],
];

const REGULATORS = [
  ['CVM', 'Companhias abertas.', 'Exige canal de denúncias e mecanismos de tratamento de irregularidades.'],
  ['BACEN · SUSEP · PREVIC · ANS', 'Setores regulados.', 'Exigem mecanismos de denúncia em diversos segmentos regulados.'],
];

const CAPABILITIES = [
  'Receber relatos com segurança e confidencialidade.',
  'Proteger o denunciante e preservar a imparcialidade da apuração.',
  'Atender às exigências legais e regulatórias.',
  'Gerar indicadores para gestão de riscos.',
  'Apoiar decisões estratégicas da alta administração.',
  'Fortalecer a cultura de ética e integridade.',
];

const PILLARS = [
  ['Tecnologia', 'Tecnologia que recebe e centraliza denúncias, organiza etapas e oferece painéis gerenciais para uma gestão mais ágil e segura.'],
  ['Especialistas em Compliance', 'Cada caso é acompanhado por especialistas do FIUS, garantindo independência, segurança jurídica e recomendações técnicas.'],
  ['Ciclo completo de gestão', 'Do recebimento da denúncia ao encerramento do caso, com rastreabilidade, histórico e gestão centralizada em um único ambiente.'],
];

const STEPS = ['Registro', 'Triagem', 'Classificação', 'Apuração', 'Tratativas', 'Indicadores'];

const DIFERENCIAIS = [
  ['Proteção de dados', 'Proteção das informações com controle de acesso, segregação de perfis e tratamento de dados em conformidade com a LGPD.'],
  ['Triagem estruturada', 'Organização inicial dos relatos por natureza, criticidade e prioridade, facilitando o direcionamento e o tratamento adequado de cada caso.'],
  ['BI executivo', 'Dashboards gerenciais com indicadores em tempo real para monitorar riscos, desempenho das apurações e apoiar a tomada de decisão.'],
  ['Conformidade', 'Apoia aderência à Lei Anticorrupção, Decreto 11.129/2022, Lei 14.457/2022, Lei 14.611/2023, LGPD e boas práticas de integridade.'],
  ['Anonimato e diálogo seguro', 'Canal seguro com comunicação anônima ou identificada, permitindo interação durante toda a apuração sem comprometer a confidencialidade.'],
  ['Contratação flexível', 'Modelos de contratação adaptáveis à realidade da empresa, acompanhando a evolução do Programa de Integridade.'],
];

const DIFERENCIAL_WIDE = [
  'Investigações conduzidas por especialistas',
  'Apurações conduzidas por advogados e especialistas em Compliance, com independência, documentação técnica e recomendações fundamentadas.',
];

const EXPERIENCE = [
  ['01', 'Intuitiva', 'Formulário guiado, simples e objetivo, desenvolvido para facilitar o relato, mesmo sem conhecimento técnico.'],
  ['02', 'Multiplataforma', 'Acesse de qualquer dispositivo, a qualquer momento, com a mesma experiência de uso.'],
  ['03', 'Rápida', 'Relate uma situação em poucos minutos, de forma segura e disponível 24 horas por dia.'],
  ['04', 'Confidencial', 'Escolha entre denúncia anônima ou identificada, com proteção das informações durante todo o processo.'],
];

const ENTRY_CHANNELS = ['Site', 'QR code', 'E-mail', 'Telefone', 'WhatsApp'];

const SCENARIOS = [
  {
    title: 'Cadeia de fornecedores',
    problem: 'Um fornecedor identifica indícios de favorecimento em um processo de compras, mas não sabe como relatar a situação com segurança.',
    solution: 'O relato é recebido, classificado e direcionado ao responsável, permitindo uma atuação rápida antes que o risco gere impactos financeiros ou reputacionais.',
  },
  {
    title: 'Ambiente de trabalho',
    problem: 'Uma colaboradora sofre assédio moral recorrente, mas teme represálias caso denuncie.',
    solution: 'Ela registra a denúncia de forma anônima, acompanha o caso pelo protocolo e a empresa recebe as informações para apuração segura e imparcial.',
  },
];

const BI_ITEMS = [
  'Tipos e classificação das denúncias;',
  'Áreas, unidades e públicos envolvidos;',
  'Status, prazos e tempo de tratamento;',
  'Recorrências, tendências e pontos de atenção.',
];

const FIUS_LEVELS = [
  ['Plataforma', 'Recebimento, organização, comunicação segura e acompanhamento das denúncias.'],
  ['Triagem especializada', 'Análise inicial, classificação de riscos e recomendação dos próximos passos.'],
  ['Investigação corporativa', 'Apuração independente, documentação técnica e recomendações fundamentadas.'],
];

const RESULTS = [
  ['Confiança', 'Ambiente seguro para denunciar, sem medo de retaliação.'],
  ['Governança', 'Controle e rastreabilidade durante toda a gestão da denúncia.'],
  ['Decisão', 'Informação qualificada para decisões rápidas e fundamentadas.'],
];

const GUARANTEES = [
  ['Anonimato', 'O denunciante escolhe entre relatar de forma identificada ou anônima, com proteção durante todo o processo.'],
  ['Controle de acesso', 'Acesso às informações controlado por perfis de usuário, garantindo que apenas pessoas autorizadas acompanhem cada caso.'],
  ['Registro de auditoria', 'Todas as etapas ficam registradas, garantindo rastreabilidade, transparência e histórico completo das tratativas.'],
  ['Confidencialidade', 'As informações são tratadas com absoluto sigilo, preservando a identidade dos envolvidos e a integridade da apuração.'],
  ['Comunicação segura', 'Permite interação segura com o denunciante, preservando o anonimato sempre que desejado.'],
];

const WHY_FIUS = [
  'Especialistas em Compliance, Investigações e LGPD;',
  'Plataforma desenvolvida para gestão de denúncias corporativas;',
  'Apoio técnico durante todo o ciclo da denúncia;',
  'Inteligência Artificial aplicada à triagem;',
  'Solução reconhecida pelo mercado.',
];

const PLAN_ITEMS = [
  'Plataforma ReportFIUS;',
  'Valor cobrado por usuário master;',
  'Implantação e treinamento inicial;',
  'Investigações corporativas contratadas sob demanda;',
  'Evolução conforme a maturidade do Programa de Integridade.',
];

const FAQ = [
  ['Por que contratar um canal de denúncias gerenciado por um terceiro independente?',
    'A gestão independente fortalece a credibilidade do canal e aumenta a confiança de colaboradores, fornecedores e demais públicos para reportarem situações de risco. Além disso, reduz potenciais conflitos de interesse, reforça a imparcialidade no recebimento das denúncias e demonstra o compromisso da empresa com ética, transparência e boas práticas de governança.'],
  ['Quais benefícios o canal traz para a gestão da empresa?',
    'Mais do que receber denúncias, o canal é uma ferramenta de gestão de riscos. Ele permite identificar precocemente fraudes, assédios, conflitos de interesse, desvios de conduta e outras irregularidades, possibilitando respostas mais rápidas, redução de perdas financeiras, fortalecimento da cultura ética e proteção da reputação da organização.'],
  ['O canal atende às exigências legais e regulatórias?',
    'Sim. Um canal de denúncias estruturado auxilia a empresa no atendimento de diversos requisitos legais e regulatórios, incluindo a Lei Anticorrupção, a Lei nº 14.457/2022 (Programa Emprega + Mulheres), normas de governança corporativa, programas de integridade e boas práticas recomendadas por órgãos reguladores nacionais e internacionais.'],
  ['Como a confidencialidade das informações é garantida?',
    'Todo o tratamento das denúncias ocorre sob rigorosos padrões de confidencialidade e segurança da informação. O acesso aos relatos é restrito às pessoas previamente autorizadas, seguindo critérios de necessidade e segregação de funções, além da observância da Lei Geral de Proteção de Dados (LGPD) e das melhores práticas de proteção de informações sensíveis.'],
  ['Como funciona o fluxo de tratamento das denúncias?',
    'Após o recebimento, cada denúncia passa por uma triagem inicial para verificação de sua aderência ao escopo do canal. Em seguida, é classificada conforme sua natureza e direcionada aos responsáveis pela apuração ou investigação, sempre com controles de confidencialidade, rastreabilidade e acompanhamento das providências adotadas.'],
  ['A empresa recebe indicadores e relatórios gerenciais?',
    'Sim. Além do tratamento individual das denúncias, o contratante recebe indicadores e relatórios gerenciais que permitem acompanhar tendências, categorias de ocorrências, áreas mais impactadas, tempo de tratamento, reincidências e outros indicadores relevantes para a gestão de riscos e para a evolução do programa de compliance.'],
  ['O canal pode ser personalizado para a realidade da empresa?',
    'Sim. O canal pode ser adaptado às características de cada organização, contemplando identidade visual, idiomas, categorias de denúncias, fluxos de encaminhamento, níveis de acesso, públicos autorizados e regras específicas de governança, de forma alinhada à estrutura e aos riscos do negócio.'],
  ['Quais canais existem?',
    'O canal de denúncias é um dos principais pilares de um programa de integridade efetivo. Ele fornece informações valiosas para identificar vulnerabilidades, aprimorar controles internos, orientar treinamentos, revisar políticas corporativas e apoiar decisões estratégicas da alta administração, contribuindo para a melhoria contínua da governança e da cultura ética da organização.'],
];

/* ------------------------------- Seções --------------------------------- */

/** `section{padding:96px 0}` do pacote, com o degrau de 72px no mobile. */
const SECTION = 'py-[72px] md:py-24';

/** Âncoras precisam limpar o header sticky de 76px. */
const ANCHOR_OFFSET = { scrollMarginTop: 76 };

const FaqItem = ({ question, answer }) => (
  <details className="group border-b border-[#009EDB]/[0.55]">
    <summary className="relative cursor-pointer list-none py-6 pr-[42px] text-xl font-extrabold text-[#009EDB] [&::-webkit-details-marker]:hidden">
      {question}
      <span
        aria-hidden="true"
        className="absolute right-0 top-5 text-[28px] font-normal leading-none text-[#111D30]"
      >
        <span className="group-open:hidden">+</span>
        <span className="hidden group-open:inline">×</span>
      </span>
    </summary>
    <p className="pb-[22px] pr-12 text-[#282A2B]">{answer}</p>
  </details>
);

/* -------------------------------- Página -------------------------------- */

const LandingPageV2 = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div
      className="bg-white text-[17px] leading-[1.58] text-[#282A2B] antialiased"
      style={{ fontFamily: '"Zalando Sans", Verdana, system-ui, sans-serif' }}
    >
      {/* ============================== HEADER ============================== */}
      <header
        className="sticky top-0 z-50 border-b border-white/10 backdrop-blur-[14px]"
        style={{ background: 'rgba(17,29,48,0.94)' }}
      >
        <Container className="flex h-[76px] items-center gap-[30px]">
          <a href="#top" aria-label="ReportFIUS" onClick={(e) => scrollToHash(e, '#top')}>
            <img
              src={IMG('fius/logo-fius-negativo.svg')}
              alt="FIUS"
              className="h-auto w-[148px] min-w-[148px]"
            />
          </a>

          {/* `max-lg:hidden` em vez de `hidden`: utilitários globais legados já
              venceram o `lg:flex` do Tailwind neste projeto. */}
          <nav aria-label="Navegação principal" className="ml-auto flex items-center gap-7 max-lg:hidden">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => scrollToHash(e, l.href)}
                className="whitespace-nowrap text-sm font-semibold text-white/85 no-underline transition hover:text-white"
              >
                {l.label}
              </a>
            ))}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm font-semibold text-white/70 transition hover:text-white"
            >
              Entrar
            </button>
          </nav>

          <Btn href={MEETING_URL} className="max-lg:ml-auto max-sm:hidden">
            Agendar reunião
          </Btn>

          <button
            type="button"
            aria-label="Abrir menu"
            aria-expanded={menuOpen}
            aria-controls="mainNav"
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-[4px] border border-white/25 p-2 text-white max-sm:ml-auto lg:hidden"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </Container>

        {menuOpen && (
          <Container id="mainNav" className="pb-4 lg:hidden">
            <nav className="flex flex-col gap-2 rounded-[6px] border border-white/[0.12] bg-[#111D30] p-[18px]">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => {
                    scrollToHash(e, l.href);
                    setMenuOpen(false);
                  }}
                  className="p-2 text-sm font-semibold text-white/85 no-underline"
                >
                  {l.label}
                </a>
              ))}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="p-2 text-left text-sm font-semibold text-white/70"
              >
                Entrar
              </button>
              <Btn href={MEETING_URL} className="mt-2 sm:hidden">
                Agendar reunião
              </Btn>
            </nav>
          </Container>
        )}
      </header>

      {/* `!p-0`: modern-ui.css dá `padding:2rem` a todo `main` e mobile-fixes.css
          crava um `padding-bottom` com `!important` — ambos furam o full-bleed. */}
      <main id="top" className="!p-0">
        {/* =============================== HERO =============================== */}
        <section
          className="relative overflow-hidden py-[58px] text-white md:py-[86px]"
          style={{
            background:
              'radial-gradient(circle at 82% 18%, rgba(0,158,219,.22), transparent 34%),' +
              'radial-gradient(circle at 20% 80%, rgba(0,69,97,.55), transparent 35%),' +
              C.navy,
          }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-[310px] -right-[180px] h-[700px] w-[700px] rounded-full border border-[#009EDB]/25"
            style={{ boxShadow: '0 0 0 80px rgba(0,158,219,.025), 0 0 0 160px rgba(0,158,219,.02)' }}
          />

          <Container className="relative z-[2] grid items-center !gap-[34px] lg:grid-cols-[0.9fr_1.1fr]">
            <Reveal className="relative pb-2.5 pt-[26px]">
              <img
                src={IMG('reportfius-logo-negativo.png')}
                alt="ReportFIUS — Plataforma de Gestão de Denúncias"
                className="mb-[18px] block h-auto w-[min(360px,100%)] max-w-[220px] sm:max-w-none"
                style={{ filter: 'drop-shadow(0 12px 24px rgba(0,0,0,.18))' }}
              />
              <H1>
                Decisões <span className="text-[#009EDB]">seguras, rápidas</span> e consistentes.
              </H1>
              <Lead className="mt-6 max-w-[650px] text-white/[0.84]">
                O ReportFIUS transforma a gestão de denúncias ao combinar tecnologia e a expertise de
                especialistas em Compliance, proporcionando decisões seguras, rápidas e consistentes.
              </Lead>

              <div className="mt-8 flex flex-wrap gap-3">
                <Btn href="#plataforma">Conhecer a plataforma</Btn>
                <Btn href={MEETING_URL} variant="ghost">
                  Agendar reunião
                </Btn>
              </div>

              <div className="mt-10 grid max-w-[560px] !gap-4 sm:grid-cols-[repeat(2,minmax(0,1fr))]">
                {[
                  ['5%', 'do faturamento anual das organizações é perdido com fraudes ocupacionais.'],
                  ['43%', 'das fraudes ocupacionais são descobertas por meio de denúncias.'],
                ].map(([value, label]) => (
                  <div key={value} className="border-l-2 border-[#009EDB] pl-4">
                    <strong className="mb-[7px] block text-[36px] leading-none text-[#009EDB]">
                      {value}
                    </strong>
                    <span className="text-sm text-white/70">{label}</span>
                  </div>
                ))}
              </div>

              <p className="mt-3.5 text-xs text-white/[0.48]">
                Fonte: Occupational Fraud – Report to the Nations 2024 | ACFE
              </p>
            </Reveal>

            <Reveal
              delay={0.1}
              className="relative grid min-h-[360px] place-items-center lg:min-h-[530px]"
            >
              <div
                aria-hidden="true"
                className="absolute h-[60%] w-[76%] rotate-[-4deg] rounded-[10px] border border-white/[0.12] backdrop-blur-[8px]"
                style={{ background: 'linear-gradient(135deg,rgba(255,255,255,.08),rgba(0,158,219,.03))' }}
              />
              <img
                src={IMG('hero-mockup.png')}
                alt="Mockup da plataforma ReportFIUS com canal de denúncias e dashboard analítico"
                className="relative z-[2] h-auto w-full max-w-[680px] lg:w-[min(780px,112%)] lg:max-w-none"
                style={{ filter: 'drop-shadow(0 28px 30px rgba(0,0,0,.32))' }}
              />
            </Reveal>
          </Container>
        </section>

        {/* ====================== POR QUE UM CANAL ====================== */}
        <section className={SECTION}>
          <Container>
            <Reveal className="grid !gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:!gap-[76px]">
              <div>
                <Eyebrow>Por que ter um canal de denúncias?</Eyebrow>
                <p
                  className="mt-5 font-light text-[#009EDB]"
                  style={{ fontSize: 'clamp(30px,4vw,50px)', lineHeight: 1.08, letterSpacing: '-0.025em' }}
                >
                  Investir em um canal de denúncias é investir no mecanismo mais eficaz para detectar
                  fraudes antes que elas gerem impactos financeiros, jurídicos e reputacionais.
                </p>
              </div>

              <div>
                <H2>Mais do que um requisito de Compliance.</H2>
                <Lead className="mt-5">
                  Hoje, o canal é um dos mecanismos mais eficazes para identificar irregularidades,
                  reduzir perdas financeiras, proteger a reputação da empresa e fornecer informações
                  estratégicas para a tomada de decisão.
                </Lead>

                <div className="mt-9 grid !gap-4 sm:grid-cols-[repeat(2,minmax(0,1fr))] lg:grid-cols-[repeat(3,minmax(0,1fr))]">
                  {BENEFITS.map(([icon, title, text]) => (
                    <article key={title} className={CARD}>
                      <img
                        src={IMG(`fius/${icon}`)}
                        alt=""
                        aria-hidden="true"
                        className="mb-[18px] h-[38px] w-[38px] object-contain"
                      />
                      <H3 className="mb-2.5" style={{ fontSize: 21 }}>
                        {title}
                      </H3>
                      <p className="text-[#6D6E71]">{text}</p>
                    </article>
                  ))}
                </div>
              </div>
            </Reveal>
          </Container>
        </section>

        {/* ========================== GOVERNANÇA ========================== */}
        <section className={`bg-[#F3F3F3] ${SECTION}`}>
          <Container>
            <div className="grid items-center !gap-14 lg:grid-cols-[1.2fr_0.8fr]">
              <Reveal>
                <Eyebrow>Governança</Eyebrow>
                <H2 className="mt-5">
                  O canal de denúncias evoluiu de uma boa prática para um requisito de governança.
                </H2>

                <div className="relative mt-9 pl-[26px]">
                  <span
                    aria-hidden="true"
                    className="absolute bottom-[9px] left-[5px] top-[9px] w-0.5 bg-[#009EDB]"
                  />
                  {TIMELINE.map(([year, badge, title, text]) => (
                    <div key={title} className="relative pb-[30px] pl-[22px]">
                      <span
                        aria-hidden="true"
                        className="absolute -left-[26px] top-2 h-3 w-3 rounded-full bg-[#009EDB] shadow-[0_0_0_5px_#F3F3F3]"
                      />
                      <div className="mb-2 flex items-center gap-3 text-[25px] font-normal text-[#009EDB]">
                        {year} <Chip>{badge}</Chip>
                      </div>
                      <H3 className="mb-1" style={{ fontSize: 19 }}>
                        {title}
                      </H3>
                      <p className="text-[15px] text-[#6D6E71]">{text}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-4 grid !gap-3 sm:grid-cols-[repeat(2,minmax(0,1fr))]">
                  {REGULATORS.map(([name, scope, text]) => (
                    <div key={name} className="rounded-[6px] border border-[#DFE3E8] bg-white p-[22px]">
                      <strong className="mb-1.5 block text-[21px] text-[#009EDB]">{name}</strong>
                      <Chip>Regulado</Chip>
                      <p className="mt-3">
                        <b>{scope}</b> {text}
                      </p>
                    </div>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.1} className="relative">
                <img
                  src={IMG('foto-reuniao.webp')}
                  alt="Profissionais em reunião em ambiente corporativo"
                  loading="lazy"
                  className="w-full rounded-[10px] object-cover"
                  style={{ aspectRatio: '4 / 5', filter: 'saturate(.9) contrast(1.03)' }}
                />
                <div className="relative -mt-12 mx-3.5 max-w-[360px] rounded-[6px] bg-[#009EDB] p-[26px] text-white shadow-[0_8px_28px_rgba(17,29,48,0.08)] lg:absolute lg:-left-[34px] lg:bottom-9 lg:mx-0 lg:mt-0">
                  <strong className="mb-2 block text-[28px] leading-[1.06]">
                    Mais do que cumprir normas:
                  </strong>
                  um canal moderno precisa gerar segurança, rastreabilidade, indicadores e confiança
                  para a tomada de decisão.
                </div>
              </Reveal>
            </div>

            <Reveal className="mt-[52px] grid items-start !gap-11 lg:grid-cols-[repeat(2,minmax(0,1fr))]">
              <div>
                <H2>Hoje, um canal de denúncias deve ser capaz de:</H2>
                <ul className="mt-7 grid !gap-[13px]">
                  {CAPABILITIES.map((t) => (
                    <li key={t} className="relative pl-7">
                      <span aria-hidden="true" className="absolute left-0 top-0 font-black text-[#009EDB]">
                        ✓
                      </span>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-[10px] bg-[#009EDB] p-[34px] text-white">
                <H3 dark style={{ fontSize: 28 }}>
                  O ReportFIUS foi desenvolvido para entregar exatamente esses resultados.
                </H3>
                <p className="mt-4">
                  O desafio deixou de ser apenas receber denúncias. Hoje, as empresas precisam
                  transformar relatos em apurações estruturadas, indicadores confiáveis e decisões
                  seguras.
                </p>
                <p className="mt-4">
                  <b>É para isso que existe o ReportFIUS.</b>
                </p>
                <Btn href="#video" variant="light" className="mt-6">
                  Assista o vídeo demonstração
                </Btn>
              </div>
            </Reveal>
          </Container>
        </section>

        {/* =========================== PLATAFORMA =========================== */}
        <section id="plataforma" className={`bg-[#111D30] text-white ${SECTION}`} style={ANCHOR_OFFSET}>
          <Container>
            <Reveal className="grid items-center !gap-[52px] lg:grid-cols-[0.9fr_1.1fr]">
              <div>
                <Eyebrow>Conheça o ReportFIUS</Eyebrow>
                <H2 dark className="mt-5">
                  Uma plataforma completa de gestão de denúncias, investigações corporativas e
                  integridade.
                </H2>
                <Lead className="mt-5 text-white/[0.76]">
                  Do primeiro relato à conclusão do caso, com o suporte técnico do time de
                  especialistas de Compliance da FIUS.
                </Lead>
              </div>

              <div className="grid !gap-3 sm:grid-cols-[repeat(2,minmax(0,1fr))]">
                {[
                  ['conheca-investigacao.png', 'Tela do Centro de Investigação do ReportFIUS em formato kanban', true],
                  ['conheca-analytics.png', 'Dashboard analítico do ReportFIUS com gráficos e indicadores', false],
                  ['conheca-canal-denuncias.png', 'Canal de denúncias do ReportFIUS com formulário de identificação', false],
                ].map(([file, alt, full]) => (
                  <img
                    key={file}
                    src={IMG(file)}
                    alt={alt}
                    loading="lazy"
                    className={
                      'rounded-[6px] border border-white/10 bg-white shadow-[0_12px_24px_rgba(0,0,0,0.15)] ' +
                      (full ? 'sm:col-span-2' : '')
                    }
                  />
                ))}
              </div>
            </Reveal>

            <Reveal className="mt-[42px] grid !gap-4 md:grid-cols-[repeat(3,minmax(0,1fr))]">
              {PILLARS.map(([title, text]) => (
                <article
                  key={title}
                  className="rounded-[6px] border border-[#009EDB]/[0.32] p-[30px]"
                  style={{ background: 'linear-gradient(180deg,rgba(0,158,219,.08),rgba(0,158,219,0))' }}
                >
                  <H3 style={{ color: C.blue }}>{title}</H3>
                  <p className="mt-2.5 text-white/[0.76]">{text}</p>
                </article>
              ))}
            </Reveal>

            {/* --------------------- COMO FUNCIONA --------------------- */}
            <Reveal
              id="como-funciona"
              className="mt-[68px] border-t border-white/[0.12] pt-[42px]"
              style={ANCHOR_OFFSET}
            >
              <div className="grid items-end !gap-[50px] lg:grid-cols-[repeat(2,minmax(0,1fr))]">
                <div>
                  <Eyebrow>Como funciona</Eyebrow>
                  <H2 dark className="mt-5">
                    Da denúncia à tomada de decisão
                  </H2>
                </div>
                <Lead className="text-white/[0.76]">
                  O ReportFIUS centraliza todas as etapas da gestão de denúncias em uma única
                  plataforma, garantindo rastreabilidade, governança, apoio especializado e
                  indicadores para tomada de decisão.
                </Lead>
              </div>

              <div className="mt-7 grid grid-cols-[repeat(2,minmax(0,1fr))] overflow-hidden rounded-[10px] bg-white text-[#282A2B] sm:grid-cols-[repeat(3,minmax(0,1fr))] lg:grid-cols-[repeat(6,minmax(0,1fr))]">
                {STEPS.map((label, i) => (
                  <div
                    key={label}
                    className={
                      'border-b border-r border-[#DFE3E8] px-2.5 py-[22px] text-center ' +
                      '[&:nth-child(2n)]:border-r-0 [&:nth-last-child(-n+2)]:border-b-0 ' +
                      'sm:[&:nth-child(2n)]:border-r sm:[&:nth-child(3n)]:border-r-0 sm:[&:nth-last-child(-n+3)]:border-b-0 ' +
                      'lg:[&:nth-child(3n)]:border-r lg:border-b-0 lg:last:border-r-0'
                    }
                  >
                    <strong className="block text-[30px] font-normal text-[#111D30]">{i + 1}</strong>
                    <span className="text-[13px] font-bold">{label}</span>
                  </div>
                ))}
              </div>

              <div className="mt-[34px] grid items-center !gap-[50px] lg:grid-cols-[1.05fr_0.95fr]">
                <img
                  src={IMG('tablet-investigacoes.webp')}
                  alt="Gestão de investigações do ReportFIUS em dispositivo móvel"
                  loading="lazy"
                  className="max-h-[370px] w-full object-contain"
                  style={{ filter: 'drop-shadow(0 20px 24px rgba(0,0,0,.22))' }}
                />
                <div>
                  <p
                    className="font-light text-[#009EDB]"
                    style={{ fontSize: 'clamp(30px,4vw,48px)', lineHeight: 1.15 }}
                  >
                    Tecnologia para registrar.
                    <br />
                    Especialistas para investigar.
                    <br />
                    Indicadores para decidir.
                  </p>
                  <Btn href={MEETING_URL} className="mt-7">
                    Agendar reunião
                  </Btn>
                </div>
              </div>
            </Reveal>
          </Container>
        </section>

        {/* ========================== DIFERENCIAIS ========================== */}
        <section id="diferenciais" className={SECTION} style={ANCHOR_OFFSET}>
          <Container>
            <Reveal>
              <Eyebrow>Diferenciais</Eyebrow>
              <H2 className="mt-5">Tecnologia + método + especialistas</H2>
            </Reveal>

            <Reveal className="mt-9 grid !gap-3.5 sm:grid-cols-[repeat(2,minmax(0,1fr))] lg:grid-cols-[repeat(4,minmax(0,1fr))]">
              {DIFERENCIAIS.map(([title, text]) => (
                <article key={title} className={`${CARD} min-h-[270px]`}>
                  <H3 style={{ color: C.blue }}>{title}</H3>
                  <p className="mt-2.5 text-[#6D6E71]">{text}</p>
                </article>
              ))}
              <article className={`${CARD} min-h-[270px] lg:col-span-2`}>
                <H3 style={{ color: C.blue }}>{DIFERENCIAL_WIDE[0]}</H3>
                <p className="mt-2.5 text-[#6D6E71]">{DIFERENCIAL_WIDE[1]}</p>
              </article>
            </Reveal>

            <Reveal className="mt-[70px] grid items-stretch !gap-14 lg:grid-cols-[0.75fr_1.25fr]">
              <img
                src={IMG('foto-smartphone.webp')}
                alt="Pessoa usando smartphone em ambiente de trabalho"
                loading="lazy"
                className="h-full min-h-[360px] w-full rounded-[10px] object-cover lg:min-h-[520px]"
              />

              <div>
                <Eyebrow>Experiência</Eyebrow>
                <H2 className="mt-5">Uma experiência pensada no usuário</H2>

                <div className="mt-7 grid !gap-6">
                  {EXPERIENCE.map(([num, title, text]) => (
                    <div key={num} className="grid grid-cols-[44px_1fr] !gap-4">
                      <span className="grid h-[42px] w-[42px] place-items-center rounded-full border border-[#009EDB] font-extrabold text-[#009EDB]">
                        {num}
                      </span>
                      <div>
                        <H3>{title}</H3>
                        <p className="mt-1.5 text-[#6D6E71]">{text}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-[30px] rounded-[10px] bg-[#111D30] p-[30px] text-white">
                  <H3 style={{ color: C.blue }}>Canais de entrada</H3>
                  <p className="mt-2.5 text-white/[0.76]">
                    Link dedicado, QR Code e layout adaptado à comunicação interna da empresa.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {ENTRY_CHANNELS.map((c) => (
                      <Chip key={c} onDark>
                        {c}
                      </Chip>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </Container>
        </section>

        {/* ====================== CENÁRIOS E DADOS ====================== */}
        <section className={`bg-[#F3F3F3] ${SECTION}`}>
          <Container>
            <Reveal>
              <Eyebrow>Como o ReportFIUS faz a diferença</Eyebrow>
              <H2 className="mt-5">
                Denúncias deixam de ser relatos isolados e passam a gerar inteligência para a gestão.
              </H2>
            </Reveal>

            <Reveal className="mt-9 grid !gap-[18px] lg:grid-cols-[repeat(2,minmax(0,1fr))]">
              {SCENARIOS.map((s) => (
                <article key={s.title} className="rounded-[6px] bg-[#111D30] p-[30px] text-white">
                  <H3 style={{ color: C.blue }}>{s.title}</H3>
                  <p className="mt-2.5 text-white/[0.76]">{s.problem}</p>
                  <span className="my-4 inline-flex rounded-full bg-[#009EDB] px-[13px] py-1 text-xs font-extrabold text-white">
                    Com o ReportFIUS
                  </span>
                  <p className="text-white/[0.76]">{s.solution}</p>
                </article>
              ))}
            </Reveal>

            <Reveal className="mt-[70px] grid items-center !gap-[52px] lg:grid-cols-[0.8fr_1.2fr]">
              <div>
                <Eyebrow>Dados para decisão</Eyebrow>
                <H2 className="mt-5">
                  Mais visibilidade para identificar riscos antes que eles se repitam.
                </H2>
                <p className="mt-5">
                  O ReportFIUS consolida informações, identifica padrões e transforma ocorrências em
                  indicadores para apoiar decisões mais rápidas e fundamentadas.
                </p>
              </div>
              <img
                src={IMG('notebook-bi.webp')}
                alt="Notebook com dashboards e gráficos do ReportFIUS"
                loading="lazy"
                className="w-full"
                style={{ filter: 'drop-shadow(0 24px 28px rgba(17,29,48,.18))' }}
              />
            </Reveal>

            <Reveal className="mt-9 grid items-center !gap-[26px] rounded-[10px] border border-[#DFE3E8] bg-white p-7 shadow-[0_8px_28px_rgba(17,29,48,0.08)] lg:grid-cols-[1.2fr_0.8fr]">
              <div>
                <strong className="text-[#009EDB]">O BI permite acompanhar:</strong>
                <ul className="mt-4 grid !gap-2.5">
                  {BI_ITEMS.map((t) => (
                    <li key={t} className="flex gap-3">
                      <span aria-hidden="true" className="mt-[10px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#009EDB]" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-[34px] font-light leading-[1.15] text-[#009EDB]">
                Mais visibilidade para identificar riscos antes que eles se repitam.
              </p>
            </Reveal>
          </Container>
        </section>

        {/* ================== NÍVEIS DE APOIO E SEGURANÇA ================== */}
        <section className={`bg-[#111D30] text-white ${SECTION}`}>
          <Container>
            <Reveal className="grid items-start !gap-14 lg:grid-cols-[0.85fr_1.15fr]">
              <div>
                <Eyebrow>Tecnologia no nível que a sua empresa precisa</Eyebrow>
                <H2 dark className="mt-5">
                  A empresa pode utilizar apenas a plataforma ou contar com o suporte dos
                  especialistas da FIUS em todas as etapas da gestão.
                </H2>
              </div>

              <div className="grid !gap-0.5 overflow-hidden rounded-[10px] bg-[#009EDB]/[0.18]">
                {FIUS_LEVELS.map(([title, text]) => (
                  <div key={title} className="bg-[#111D30] p-7">
                    <H3 style={{ color: C.blue, fontSize: 20 }}>{title}</H3>
                    <p className="mt-2.5 text-white/[0.76]">{text}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal className="mt-[42px] grid !gap-px overflow-hidden rounded-[10px] bg-[#DFE3E8] sm:grid-cols-[repeat(3,minmax(0,1fr))]">
              {RESULTS.map(([title, text]) => (
                <div key={title} className="bg-white p-7 text-center text-[#282A2B]">
                  <strong className="block text-[31px] font-light text-[#009EDB]">{title}</strong>
                  <span className="mt-1.5 block">{text}</span>
                </div>
              ))}
            </Reveal>

            <Reveal className="mt-16">
              <H2 dark>O que isso gera para quem denuncia e para a empresa?</H2>
            </Reveal>

            <Reveal className="mt-14 grid !gap-3.5 sm:grid-cols-[repeat(2,minmax(0,1fr))] lg:grid-cols-[repeat(3,minmax(0,1fr))]">
              {GUARANTEES.map(([title, text]) => (
                <article
                  key={title}
                  className="rounded-[6px] border border-[#009EDB]/[0.38] p-[26px] text-white"
                >
                  <H3 style={{ color: C.blue, fontSize: 21 }}>{title}</H3>
                  <p className="mt-2.5 text-white/[0.76]">{text}</p>
                </article>
              ))}
              <article className="rounded-[6px] border border-[#009EDB]/[0.38] p-[26px]">
                <H3 style={{ color: C.blue, fontSize: 21 }}>
                  Transparência para quem denuncia.
                  <br />
                  Segurança para quem apura.
                </H3>
              </article>
            </Reveal>

            <Reveal className="mt-[46px] flex flex-col items-start justify-between gap-7 rounded-[10px] bg-[#009EDB] p-[34px] sm:flex-row sm:items-center">
              <div>
                <H3 dark>Por que escolher o ReportFIUS?</H3>
                <ul className="mt-4 list-disc pl-5">
                  {WHY_FIUS.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </div>
              <img
                src={IMG('selo-dna-fenalaw-2026.webp')}
                alt="Prêmio Análise DNA+ Fenalaw 2026"
                loading="lazy"
                className="w-[150px] shrink-0"
              />
            </Reveal>
          </Container>
        </section>

        {/* ============================= PLANOS ============================= */}
        <section id="planos" className={SECTION} style={ANCHOR_OFFSET}>
          <Container>
            <Reveal>
              <Eyebrow>Investimento</Eyebrow>
              <H2 className="mt-5">Modalidades de contratação</H2>
            </Reveal>

            <Reveal className="mt-10 grid !gap-[18px] lg:grid-cols-[repeat(2,minmax(0,1fr))]">
              {[
                {
                  name: 'Plano mensal',
                  featured: false,
                  lead: 'Flexibilidade para iniciar rapidamente e evoluir conforme a necessidade da empresa.',
                  note: null,
                },
                {
                  name: 'Plano anual',
                  featured: true,
                  lead: 'Maior previsibilidade financeira e melhores condições para projetos de longo prazo.',
                  note: 'Desconto para pagamento anual à vista.',
                },
              ].map((plan) => (
                <article
                  key={plan.name}
                  className={
                    'relative rounded-[6px] border p-[34px] ' +
                    (plan.featured
                      ? 'border-[#111D30] bg-[#111D30] text-white'
                      : 'border-[#DFE3E8] bg-white')
                  }
                >
                  {plan.featured && (
                    <span className="absolute -top-3.5 right-[18px] rounded-full bg-[#009EDB] px-3.5 py-1.5 text-[11px] font-extrabold text-white">
                      Recomendado
                    </span>
                  )}
                  <H3
                    dark={plan.featured}
                    style={{ fontSize: 19, textTransform: 'uppercase', letterSpacing: '0.04em' }}
                  >
                    {plan.name}
                  </H3>
                  <Btn href={MEETING_URL} className="mt-5">
                    Saber valor
                  </Btn>
                  <p className="my-6 text-[22px] font-bold leading-[1.25] text-[#009EDB]">{plan.lead}</p>
                  <ul className={'grid list-disc !gap-2.5 pl-5 ' + (plan.featured ? 'text-white/85' : '')}>
                    {PLAN_ITEMS.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                  {plan.note && <p className="mt-4 text-sm text-white/70">{plan.note}</p>}
                </article>
              ))}
            </Reveal>

            <Reveal>
              <Lead className="mt-[26px]">
                Entre em contato para conhecer o modelo mais adequado às necessidades da sua empresa.
              </Lead>
            </Reveal>

            <Reveal className="mt-10 grid place-items-center">
              <img
                src={IMG('phones-fluxo.webp')}
                alt="Fluxo do canal de denúncias ReportFIUS em smartphones"
                loading="lazy"
                /* `w-full` junto do teto de 820px: sozinho, o `max-w-` vence o
                   `max-width:100%` do preflight e a arte vaza no mobile. */
                className="w-full max-w-[820px]"
                style={{ filter: 'drop-shadow(0 20px 18px rgba(17,29,48,.16))' }}
              />
            </Reveal>
          </Container>
        </section>

        {/* ============================= VÍDEO ============================= */}
        <section
          id="video"
          className={`relative overflow-hidden bg-[#111D30] text-white ${SECTION}`}
          style={ANCHOR_OFFSET}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-[230px] -top-[250px] h-[520px] w-[520px] rounded-full border border-[#009EDB]/[0.16]"
            style={{ boxShadow: '0 0 0 72px rgba(0,158,219,.025)' }}
          />
          <Container className="relative z-[1]">
            <Reveal className="mb-[34px] grid items-end !gap-[54px] lg:grid-cols-[0.8fr_1.2fr]">
              <div>
                <Eyebrow>Demonstração</Eyebrow>
                <H2 dark className="mt-5">
                  Veja o ReportFIUS em ação
                </H2>
              </div>
              <Lead className="text-white/[0.74]">
                Conheça a experiência da plataforma e veja como o ReportFIUS organiza o ciclo da
                denúncia, da entrada do relato à gestão das apurações e dos indicadores.
              </Lead>
            </Reveal>

            <Reveal
              className="relative w-full overflow-hidden rounded-[10px] border border-[#009EDB]/[0.32] bg-[#080f1b] shadow-[0_24px_60px_rgba(0,0,0,0.28)]"
              style={{ aspectRatio: '16 / 9' }}
            >
              <iframe
                src={VIDEO_EMBED_URL}
                title="Demonstração do ReportFIUS"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 h-full w-full border-0"
              />
            </Reveal>

            <Reveal className="mt-5 flex flex-wrap items-center justify-between gap-6 text-sm text-white/[0.62]">
              <span>Vídeo de demonstração do ReportFIUS</span>
              <Btn href={VIDEO_WATCH_URL} variant="ghost">
                Abrir no YouTube
              </Btn>
            </Reveal>
          </Container>
        </section>

        {/* ============================== FAQ ============================== */}
        <section id="faq" className={`bg-[#F3F3F3] ${SECTION}`} style={ANCHOR_OFFSET}>
          <Container>
            <Reveal>
              <Eyebrow>Perguntas e respostas rápidas</Eyebrow>
              <H2 className="mt-5">
                As dúvidas mais comuns respondidas de forma objetiva antes da demonstração.
              </H2>
            </Reveal>

            <Reveal className="mt-[34px] border-t border-[#009EDB]">
              {FAQ.map(([q, a]) => (
                <FaqItem key={q} question={q} answer={a} />
              ))}
            </Reveal>
          </Container>
        </section>

        {/* ============================= CONTATO ============================= */}
        <section
          id="contato"
          className="relative overflow-hidden bg-[#111D30] py-[72px] text-white md:py-[92px]"
          style={ANCHOR_OFFSET}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.34] mix-blend-screen"
            style={{
              backgroundImage: `url(${IMG('cta-textura.webp')})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center right',
            }}
          />
          <Container className="relative z-[2] grid items-center !gap-12 lg:grid-cols-[0.8fr_1.2fr]">
            <Reveal>
              <Eyebrow>ReportFIUS</Eyebrow>
              <H2 dark className="mt-5">
                Quer conhecer melhor o ReportFIUS?
              </H2>
              <Lead className="mt-5 text-white/[0.76]">
                Converse com nosso time e conheça a plataforma em uma demonstração.
              </Lead>
            </Reveal>

            <Reveal
              delay={0.1}
              className="max-w-[580px] rounded-[10px] border border-[#009EDB]/[0.32] p-9"
              style={{
                background: 'linear-gradient(135deg,rgba(0,69,97,.92),rgba(17,29,48,.94))',
              }}
            >
              <H3 style={{ color: C.blue }}>Agendar reunião</H3>
              <p className="mt-2">Converse com nosso time</p>

              <div className="mt-3 flex items-center gap-3 text-lg sm:text-[23px]">
                <Mail aria-hidden="true" className="h-5 w-5 shrink-0 text-[#009EDB]" />
                <a href={`mailto:${EMAIL}`} className="no-underline hover:underline">
                  {EMAIL}
                </a>
              </div>
              <div className="mt-3 flex items-center gap-3 text-lg sm:text-[23px]">
                <Phone aria-hidden="true" className="h-5 w-5 shrink-0 text-[#009EDB]" />
                <a href={`tel:${PHONE_HREF}`} className="no-underline hover:underline">
                  {PHONE_LABEL}
                </a>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Btn href={MEETING_URL}>Agendar reunião</Btn>
                <Btn href="#video" variant="accent">
                  Assista o vídeo demonstração
                </Btn>
              </div>
            </Reveal>
          </Container>
        </section>
      </main>

      {/* ============================== FOOTER ============================== */}
      <footer className="bg-[#0c1625] py-[30px] text-white/[0.68]">
        <Container className="flex flex-col justify-between gap-[30px] text-[13px] md:flex-row md:items-center">
          <div className="flex flex-col gap-3">
            <img
              src={IMG('reportfius-logo-negativo.png')}
              alt="ReportFIUS"
              loading="lazy"
              className="h-auto w-[min(280px,100%)] max-w-[220px] sm:max-w-none"
            />
            <span className="max-w-[720px]">
              ReportFIUS · Plataforma de Gestão de Denúncias · Conteúdo estruturado a partir do
              material institucional fornecido.
            </span>
          </div>
          <span>
            FIUS · Tecnologia e especialistas em Compliance para uma gestão de denúncias mais segura.
          </span>
        </Container>
      </footer>
    </div>
  );
};

export default LandingPageV2;
